import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle, StatCard } from "@/components/GlassBits";
import { formatINR, formatDate, statusBadgeClass } from "@/lib/format";
import { ArrowLeft, Undo2, Clock, X, Copy, Plus, ExternalLink } from "lucide-react";
import { sounds } from "@/lib/sound";

const REFUND_WINDOW_HOURS = 48;

function hoursSince(iso) {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  return (Date.now() - t) / (1000 * 60 * 60);
}

export default function CustomerDetail() {
  const { email } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const [linkAmount, setLinkAmount] = useState("");
  const [linkBranch, setLinkBranch] = useState("");
  const [linkPurpose, setLinkPurpose] = useState("");
  const [createdLink, setCreatedLink] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualProof, setManualProof] = useState(null);
  const [manualForm, setManualForm] = useState({ amount: "", method: "upi", purpose: "", category: "", branch: "", payment_date: new Date().toISOString().slice(0, 10), reference: "" });
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/customers/${encodeURIComponent(email)}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  useEffect(() => {
    api.get("/admin/branches")
      .then(({ data }) => setBranches(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load branches"));
  }, []);

  const openRefund = (payment) => {
    setActive(payment);
    setAmount(String(payment.amount || ""));
    setReason("");
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/admin/refunds/${active.payment_id}`, { reason, amount: parseFloat(amount) });
      toast.success("Refund issued");
      sounds.refund();
      setActive(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };

  const submitCreatePayment = async (e) => {
    e.preventDefault();

    const amountValue = Number(linkAmount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error("Enter a valid amount greater than zero");
      return;
    }

    if (!linkBranch.trim() || !linkPurpose.trim()) {
      toast.error("Branch and purpose are required");
      return;
    }

    setCreateBusy(true);
    try {
      const { data: result } = await api.post("/admin/payment-links", {
        customer_name: profile.name || profile.email,
        email: profile.email,
        phone: profile.phone || "",
        amount: amountValue,
        branch: linkBranch.trim(),
        purpose: linkPurpose.trim(),
        currency: "INR",
        expiry_days: 7,
        partial_payment: false,
      });

      setCreatedLink(result);
      setCreateOpen(false);
      setLinkAmount("");
      setLinkPurpose("");
      setLinkBranch(profile.branch || "");
      toast.success(`Payment link created: ${result.invoice_no}`);
      sounds.linkCreated();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create payment link");
    } finally {
      setCreateBusy(false);
    }
  };


  const submitManualPayment = async (e) => {
    e.preventDefault();
    const value = Number(manualForm.amount);
    if (!Number.isFinite(value) || value <= 0 || !manualForm.branch.trim() || !manualForm.purpose.trim()) {
      toast.error("Enter a valid amount, branch and purpose");
      return;
    }
    setManualBusy(true);
    try {
      const body = new FormData();
      body.append("customer_email", profile.email);
      body.append("customer_name", profile.name || profile.email);
      body.append("phone", profile.phone || "");
      body.append("amount", String(value));
      body.append("method", manualForm.method);
      body.append("purpose", manualForm.purpose.trim());
      body.append("category", manualForm.category.trim());
      body.append("branch", manualForm.branch.trim());
      body.append("payment_date", `${manualForm.payment_date}T12:00:00`);
      body.append("reference", manualForm.reference.trim());
      if (manualProof) body.append("proof", manualProof);
      await api.post("/admin/manual-payments", body);
      toast.success("Manual payment recorded successfully");
      setManualOpen(false);
      setManualProof(null);
      setManualForm({ amount: "", method: "upi", purpose: "", category: "", branch: profile.branch || "", payment_date: new Date().toISOString().slice(0, 10), reference: "" });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((item) => item?.msg || JSON.stringify(item)).join(", ")
          : "Failed to submit manual payment";
      toast.error(message);
    } finally {
      setManualBusy(false);
    }
  };

  const copyCreatedLink = async () => {
    if (!createdLink?.payment_link) {
      toast.error("No payment URL was returned");
      return;
    }
    try {
      await navigator.clipboard.writeText(createdLink.payment_link);
      toast.success("Payment link copied");
    } catch {
      toast.error("Could not copy the link. Open the payment URL instead.");
    }
  };

  if (!data) return <div className="text-white/50">Loading…</div>;

  const { profile, stats, transactions, invoices } = data;

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white inline-flex items-center gap-2 mb-3" data-testid="cd-back">
        <ArrowLeft size={14}/> Back
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle eyebrow="Customer" title={profile.name || profile.email} />
        <button
          type="button"
          onClick={() => {
            setLinkAmount("");
            setLinkBranch(profile.branch || "");
            setLinkPurpose("");
            setCreateOpen(true);
          }}
          className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"
          data-testid="cd-create-payment-btn"
        >
          <Plus size={16} /> Create Payment
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="ldc-glass p-6 md:col-span-1">
          <div className="w-16 h-16 rounded-full bg-[#fbce18]/20 flex items-center justify-center text-[#fbce18] font-display text-2xl font-semibold">
            {(profile.name || profile.email)[0]?.toUpperCase()}
          </div>
          <div className="mt-4 font-display text-lg">{profile.name || "—"}</div>
          <div className="text-sm text-white/70">{profile.email}</div>
          <div className="mt-4 space-y-1 text-sm text-white/60">
            <div>📱 {profile.phone || "—"}</div>
            <div>🏬 {profile.branch || "—"}</div>
            {profile.company_name && <div>🏢 {profile.company_name}</div>}
            {profile.gst_number && <div>🧾 {profile.gst_number}</div>}
          </div>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          <StatCard testid="cd-stat-paid" label="Total Paid" value={formatINR(stats.total_paid)} sub={`${stats.total_transactions} transactions`} accent />
          <StatCard testid="cd-stat-pending" label="Pending" value={formatINR(stats.pending_amount)} sub={`${stats.pending_count} invoices`} />
          <StatCard testid="cd-stat-invoices" label="Total Invoices" value={stats.total_invoices} />
          <StatCard testid="cd-stat-refunds" label="Refunds" value={formatINR(stats.refund_amount)} sub={`${stats.refund_count} refunded`} />
        </div>
      </div>

      {createdLink && (
        <div className="ldc-glass p-5 mt-5" data-testid="cd-created-payment">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#fbce18]/80">
                Payment link created
              </div>
              <div className="font-display text-lg mt-1">
                Invoice <span className="text-[#fbce18]">{createdLink.invoice_no}</span>
              </div>
              <div className="text-sm text-white/60 mt-1">
                {formatINR(createdLink.amount, createdLink.currency)} · {createdLink.purpose}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCreatedLink(null)}
              className="p-1.5 rounded hover:bg-white/5 text-white/60"
              aria-label="Dismiss payment link"
            >
              <X size={16} />
            </button>
          </div>
          {createdLink.payment_link ? (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <div className="ldc-input min-w-0 flex-1 rounded-md px-3 py-2 text-sm break-all">
                {createdLink.payment_link}
              </div>
              <button
                type="button"
                onClick={copyCreatedLink}
                className="ldc-btn-primary px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"
                data-testid="cd-copy-created-payment"
              >
                <Copy size={14} /> Copy
              </button>
              <a
                href={createdLink.payment_link}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-2 rounded-md border border-white/10 text-sm inline-flex items-center gap-2"
              >
                <ExternalLink size={14} /> Open
              </a>
            </div>
          ) : (
            <p className="text-sm text-red-300 mt-3">
              The invoice was returned without a payment URL. Check the payment-link status below.
            </p>
          )}
        </div>
      )}

      <div className="mt-8">

      <div className="mt-6 ldc-glass p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium">Record an offline payment</div>
          <p className="text-xs text-white/50 mt-1">UPI or cash received directly. Receipt optional.</p>
        </div>
        <button type="button" onClick={() => {
          setManualProof(null);
          setManualForm({ amount: "", method: "upi", purpose: "", category: "", branch: profile.branch || "", payment_date: new Date().toISOString().slice(0, 10), reference: "" });
          setManualOpen(true);
        }} className="px-4 py-2 rounded-md border border-white/15 text-sm inline-flex items-center gap-2"
          data-testid="cd-manual-payment-open">
          <Plus size={15}/> Manual Payment
        </button>
      </div>

      {manualOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !manualBusy && setManualOpen(false)}>
          <form onSubmit={submitManualPayment} onClick={(e) => e.stopPropagation()}
            className="ldc-glass p-5 md:p-6 w-full max-w-xl my-6">
            <div className="flex items-center justify-between mb-5">
              <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#fbce18]/80">Offline transaction</div>
                <h3 className="font-display text-xl">Manual Payment</h3>
                <p className="text-xs text-white/50 mt-1">{profile.name || profile.email}</p></div>
              <button type="button" disabled={manualBusy} onClick={() => setManualOpen(false)} className="p-1.5 rounded hover:bg-white/5"><X size={18}/></button>
            </div>
            <p className="text-xs text-white/50 mb-4">This payment is recorded immediately and included in paid totals.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Amount (INR) *</span>
                <input required type="number" min="0.01" max="100000000" step="0.01" value={manualForm.amount}
                  onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></label>
              <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Method *</span>
                <select value={manualForm.method} onChange={(e) => setManualForm({ ...manualForm, method: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"><option value="upi">UPI</option><option value="cash">Cash</option></select></label>
              <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Branch *</span>
                <select required value={manualForm.branch} onChange={(e) => setManualForm({ ...manualForm, branch: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"><option value="">Select branch</option>
                  {branches.map((b) => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}</select></label>
              <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Payment date *</span>
                <input required type="date" value={manualForm.payment_date} onChange={(e) => setManualForm({ ...manualForm, payment_date: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></label>
              <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Category</span>
                <select value={manualForm.category} onChange={(e) => setManualForm({ ...manualForm, category: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm">
                  <option value="">Select category</option>
                  {["General", "Subscription", "Product", "Service", "Consulting", "Other"].map((c) =>
                    <option key={c} value={c}>{c}</option>
                  )}
                </select></label>
              <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Reference / UPI ID</span>
                <input maxLength={200} value={manualForm.reference} onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></label>
              <label className="block md:col-span-2"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Purpose *</span>
                <textarea required maxLength={500} rows={2} value={manualForm.purpose} onChange={(e) => setManualForm({ ...manualForm, purpose: e.target.value })}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></label>
              <label className="block md:col-span-2"><span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Proof / receipt (optional; max 8 MB)</span>
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => setManualProof(e.target.files?.[0] || null)}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"/>
                {manualProof && <span className="block text-xs text-white/50 mt-1">{manualProof.name}</span>}</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" disabled={manualBusy} onClick={() => setManualOpen(false)}
                className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
              <button type="submit" disabled={manualBusy}
                className="ldc-btn-primary px-4 py-2 rounded-md text-sm">{manualBusy ? "Saving…" : "Record payment"}</button>
            </div>
          </form>
        </div>
      )}

        <SectionTitle eyebrow="Ledger" title="Transactions" />
        <div className="ldc-glass overflow-hidden">
          <div className="overflow-x-auto ldc-scroll">
            <table className="w-full ldc-table" data-testid="cd-transactions">
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>Invoice</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Date</th>
                  <th>Refund</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-white/40">No transactions.</td></tr>)}
                {transactions.map((t) => {
                  const hrs = hoursSince(t.created_at);
                  const captured = t.status === "captured";
                  const refundable = !t.is_manual && captured && hrs < REFUND_WINDOW_HOURS;
                  const remaining = Math.max(0, REFUND_WINDOW_HOURS - hrs).toFixed(1);
                  return (
                    <tr key={t.payment_id} data-testid={`cd-tx-${t.payment_id}`}>
                      <td className="font-mono text-xs">{t.payment_id}</td>
                      <td className="font-mono text-[#fbce18]">{t.invoice_no || "—"}</td>
                      <td className="font-medium">{formatINR(t.amount)}</td>
                      <td><span className={statusBadgeClass(t.status)}>{t.status}</span></td>
                      <td className="uppercase text-xs">{t.method || "—"}</td>
                      <td className="text-xs text-white/50">{formatDate(t.created_at)}</td>
                      <td>
                        {t.status === "refunded" ? (
                          <span className="ldc-badge ldc-badge-refunded">Refunded</span>
                        ) : refundable ? (
                          <button onClick={() => openRefund(t)} className="ldc-btn-primary px-3 py-1 rounded-md text-xs inline-flex items-center gap-1.5" data-testid={`cd-refund-btn-${t.payment_id}`}>
                            <Undo2 size={12}/> Issue Refund
                          </button>
                        ) : captured ? (
                          <span className="text-xs text-white/40 inline-flex items-center gap-1"><Clock size={12}/> Expired</span>
                        ) : (
                          <span className="text-xs text-white/40">—</span>
                        )}
                        {refundable && <div className="text-[10px] text-white/40 mt-1">{remaining}h left</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <SectionTitle eyebrow="Invoices" title="Payment links & invoices" />
        <div className="ldc-glass overflow-hidden">
          <div className="overflow-x-auto ldc-scroll">
            <table className="w-full ldc-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Purpose</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (<tr><td colSpan={6} className="text-center py-10 text-white/40">No invoices.</td></tr>)}
                {invoices.map((i) => (
                  <tr key={i.invoice_no}>
                    <td className="font-mono text-[#fbce18]">{i.invoice_no}</td>
                    <td>{i.purpose}</td>
                    <td>{formatINR(i.amount, i.currency)}</td>
                    <td><span className={statusBadgeClass(i.status)}>{i.status}</span></td>
                    <td className="text-xs text-white/50">{formatDate(i.created_at)}</td>
                    <td><a href={`/invoice/${i.invoice_no}`} target="_blank" rel="noreferrer" className="text-xs text-[#fbce18]">View</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {createOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !createBusy && setCreateOpen(false)}
        >
          <form
            onSubmit={submitCreatePayment}
            className="ldc-glass p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="cd-create-payment-form"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#fbce18]/80">
                  New invoice
                </div>
                <h3 className="font-display text-xl">Create Payment</h3>
                <p className="text-xs text-white/50 mt-1">{profile.name || profile.email}</p>
              </div>
              <button
                type="button"
                disabled={createBusy}
                onClick={() => setCreateOpen(false)}
                className="p-1.5 rounded hover:bg-white/5"
                aria-label="Close create payment form"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">
                  Amount (INR) *
                </div>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={linkAmount}
                  onChange={(e) => setLinkAmount(e.target.value)}
                  required
                  autoFocus
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"
                  placeholder="Enter amount"
                  data-testid="cd-create-payment-amount"
                />
              </label>

              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">
                  Branch *
                </div>
                <select
                  value={linkBranch}
                  onChange={(e) => setLinkBranch(e.target.value)}
                  required
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"
                  data-testid="cd-create-payment-branch"
                >
                  <option value="">Select branch</option>
                  {branches.map((b) => (
                    <option key={b.id || b.name} value={b.name}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">
                  Purpose *
                </div>
                <textarea
                  rows={3}
                  value={linkPurpose}
                  onChange={(e) => setLinkPurpose(e.target.value)}
                  required
                  maxLength={500}
                  className="ldc-input w-full rounded-md px-3 py-2 text-sm"
                  placeholder="What is this payment for?"
                  data-testid="cd-create-payment-purpose"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                disabled={createBusy}
                onClick={() => setCreateOpen(false)}
                className="px-4 py-2 rounded-md border border-white/10 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createBusy}
                className="ldc-btn-primary px-5 py-2 rounded-md text-sm inline-flex items-center gap-2"
                data-testid="cd-create-payment-submit"
              >
                <Plus size={14} />
                {createBusy ? "Creating…" : "Create Payment Link"}
              </button>
            </div>
          </form>
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <form onSubmit={submitRefund} className="ldc-glass p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">Issue refund</h3>
              <button type="button" onClick={() => setActive(null)} className="p-1.5 rounded hover:bg-white/5"><X size={18}/></button>
            </div>
            <div className="text-sm text-white/70 space-y-1 mb-4">
              <div>Invoice: <span className="font-mono text-[#fbce18]">{active.invoice_no}</span></div>
              <div>Amount captured: {formatINR(active.amount)}</div>
            </div>
            <div className="space-y-3">
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Refund amount</div>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="cd-refund-amount" />
              </label>
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Reason</div>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="cd-refund-reason" />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setActive(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="cd-refund-confirm">
                {busy ? "Processing…" : "Confirm refund"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
